import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { useCompaniesStore } from "../../shared/store/companies.store";
import { useAuthStore } from "../../shared/store/auth.store";
import { CompaniesService } from "../../shared/services/companies.service";
import { ButtonLarge, ButtonLink, PopUp } from "../../shared/uikit";

const STATUS_STYLES: Record<
  string,
  { bg: string; color: string; icon: string; label: string }
> = {
  pending: {
    bg: "#f0a030",
    color: "var(--first-color-inv)",
    icon: "🟡",
    label: "Pending",
  },
  approved: { bg: "#2a9d2a", color: "#fff", icon: "🟢", label: "Approved" },
  rejected: { bg: "#d93030", color: "#fff", icon: "🔴", label: "Rejected" },
  in_progress: {
    bg: "#1a73e8",
    color: "#fff",
    icon: "🔵",
    label: "In Progress",
  },
};

const getStatusStyle = (status: string) =>
  STATUS_STYLES[status] ?? {
    bg: "var(--second-background)",
    color: "var(--second-color)",
    icon: "⚪",
    label: status,
  };

const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

export const CompanyDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const companyId = Number(params.id);

  // Подписываемся на companies из стора, чтобы страница автоматически
  // обновлялась при загрузке данных через API (например, после регистрации).
  const companies = useCompaniesStore((s) => s.companies);
  const company = companies.find((c) => c.id === companyId);
  // Реактивная подписка на instanceId — при перелогине (например, после
  // переподключения по QR) компонент перерисуется и кнопка approve появится.
  const currentUserId = useAuthStore((s) => s.instanceId);

  // Fallback: если компания не найдена в store (прямой переход на страницу,
  // перезапуск приложения), загружаем актуальный список компаний с API.
  const [isFallbackLoading, setIsFallbackLoading] = useState(false);
  const [fallbackError, setFallbackError] = useState<string | null>(null);
  // Guard: предотвращает повторные fallback-запросы, если компания
  // не существует на сервере (иначе useEffect зациклится).
  const hasAttemptedFallback = useRef(false);

  // Approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  // Leave company state (member, не admin)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const handleLeaveCompany = async () => {
    setLeaveLoading(true);
    setLeaveError(null);
    if (!company) return;
    try {
      await CompaniesService.leaveCompany(company.id);
      setShowLeaveConfirm(false);
      // Удаляем компанию из стора и возвращаемся в профиль
      const { setCompanies, companies: currentCompanies } =
        useCompaniesStore.getState();
      setCompanies(currentCompanies.filter((c) => c.id !== company.id));
      navigate(getPath(ConfigRoutes.PROFILE));
    } catch (err) {
      setLeaveError(
        err instanceof Error ? err.message : "Failed to leave company",
      );
    } finally {
      setLeaveLoading(false);
    }
  };

  // Fallback: если компании нет в сторе — загружаем с API
  useEffect(() => {
    if (!Number.isNaN(companyId) && !company && !hasAttemptedFallback.current) {
      hasAttemptedFallback.current = true;
      setIsFallbackLoading(true);
      setFallbackError(null);
      CompaniesService.getCompanies()
        .then((fetchedCompanies) => {
          const { setCompanies } = useCompaniesStore.getState();
          setCompanies(fetchedCompanies);
        })
        .catch((err) => {
          const message =
            err instanceof Error ? err.message : "Failed to load companies";
          setFallbackError(message);
        })
        .finally(() => setIsFallbackLoading(false));
    }
  }, [companyId, company]);

  // Некорректный ID в URL (например, /companies/abc)
  if (Number.isNaN(companyId)) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Company Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>❓</p>
          <p style={{ color: "var(--second-color)" }}>Invalid company ID</p>
        </div>
        <ButtonLarge
          title="← Back to Profile"
          onClick={() => navigate(getPath(ConfigRoutes.PROFILE))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Fallback-загрузка данных с сервера
  if (isFallbackLoading) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Company Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "var(--second-color)",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⏳</p>
          <p>Loading company...</p>
        </div>
      </div>
    );
  }

  // Company not found
  if (!company) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Company Details</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔍</p>
          <p style={{ color: "var(--second-color)" }}>
            Company not found (ID: {companyId})
          </p>
          <p
            style={{
              color: "var(--second-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            The company may have been removed or you may not have access.
          </p>
          {fallbackError && (
            <p
              style={{
                color: "var(--accent-color)",
                fontSize: "14px",
                marginTop: "0.5rem",
              }}
            >
              Error: {fallbackError}
            </p>
          )}
        </div>
        <ButtonLarge
          title="← Back to Profile"
          onClick={() => navigate(getPath(ConfigRoutes.PROFILE))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  const statusStyle = getStatusStyle(company.status);
  const adminIds = company.admin_ids ?? [];
  const instanceIds = company.instance_ids ?? [];
  const isAdmin = currentUserId !== null && adminIds.includes(currentUserId);

  const handleApprove = async () => {
    setApproveError(null);
    setApproveLoading(true);
    try {
      const updated = await CompaniesService.approveCompany(company.id);
      // Update the company in the store with the new data.
      // Берём актуальный список из стора, а не из замыкания рендера,
      // чтобы не потерять компании, загруженные параллельно.
      const { setCompanies, companies: currentCompanies } =
        useCompaniesStore.getState();
      const updatedCompanies = currentCompanies.map((c) =>
        c.id === company.id ? updated : c,
      );
      setCompanies(updatedCompanies);
      setShowApproveModal(false);
      setApproveError(null);
      // После approve автоматически обновляем список, чтобы instance_ids
      // и статус компании точно совпадали с сервером.
      CompaniesService.getCompanies()
        .then((fetched) => {
          useCompaniesStore.getState().setCompanies(fetched);
        })
        .catch(() => {
          // Молча — основное обновление уже применено выше.
        });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to approve company";
      setApproveError(message);
    } finally {
      setApproveLoading(false);
    }
  };

  // Build status timeline
  const timelineEntries: {
    status: string;
    date: string | undefined | null;
  }[] = [{ status: "created", date: company.created_at }];
  if (
    company.status !== "pending" ||
    company.updated_at !== company.created_at
  ) {
    timelineEntries.push({ status: "pending", date: company.updated_at });
  }
  if (company.status === "approved" || company.status === "rejected") {
    timelineEntries.push({ status: company.status, date: company.updated_at });
  }

  return (
    <div style={{ padding: "1rem" }}>
      <h2>Company Details</h2>

      {/* Company header card */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1.5rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          background: "var(--second-background)",
        }}
      >
        {/* Company name + logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              flexShrink: 0,
              border: "1px solid var(--border-color)",
              overflow: "hidden",
              background: "var(--first-background)",
            }}
          >
            {company.logo ? (
              <img
                src={company.logo}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              "🏢"
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0 }}>{company.name}</h3>
            <span style={{ fontSize: "13px", color: "var(--second-color)" }}>
              ID: {company.id}
            </span>
          </div>
        </div>

        {/* Status badge */}
        <div
          style={{
            display: "inline-block",
            padding: "4px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            background: statusStyle.bg,
            color: statusStyle.color,
            marginBottom: "1rem",
          }}
        >
          {statusStyle.icon} {statusStyle.label}
        </div>

        {/* Details table */}
        <table style={{ width: "100%", fontSize: "14px", marginTop: "0.5rem" }}>
          <tbody>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                INN
              </td>
              <td style={{ padding: "0.5rem" }}>{company.inn}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Address
              </td>
              <td style={{ padding: "0.5rem" }}>{company.address || "—"}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Director
              </td>
              <td style={{ padding: "0.5rem" }}>{company.director || "—"}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Phone
              </td>
              <td style={{ padding: "0.5rem" }}>{company.telephone || "—"}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Email
              </td>
              <td style={{ padding: "0.5rem" }}>{company.email || "—"}</td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Description
              </td>
              <td style={{ padding: "0.5rem" }}>
                {company.description || "—"}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Created
              </td>
              <td style={{ padding: "0.5rem" }}>
                {formatDate(company.created_at)}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  color: "var(--second-color)",
                  padding: "0.5rem 0.5rem 0.5rem 0",
                }}
              >
                Updated
              </td>
              <td style={{ padding: "0.5rem" }}>
                {formatDate(company.updated_at)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Instance info (if approved) */}
      {company.status === "approved" && instanceIds.length > 0 && (
        <div
          style={{
            marginTop: "1rem",
            padding: "1rem 1.25rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            background: "var(--first-background)",
          }}
        >
          <h3 style={{ margin: "0 0 0.5rem" }}>📦 Instances</h3>
          <p
            style={{
              fontSize: "13px",
              color: "var(--second-color)",
              margin: "0 0 0.5rem",
            }}
          >
            {instanceIds.length} instance
            {instanceIds.length !== 1 ? "s" : ""} associated
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {instanceIds.map((id) => (
              <span
                key={id}
                style={{
                  display: "inline-block",
                  padding: "3px 10px",
                  background: "var(--second-background)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  border: "1px solid var(--border-color)",
                }}
              >
                Instance #{id}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Admin list */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem 1.25rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          background: "var(--first-background)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem" }}>👤 Admins</h3>
        <p
          style={{ fontSize: "13px", color: "var(--second-color)", margin: 0 }}
        >
          {adminIds.length} admin
          {adminIds.length !== 1 ? "s" : ""} assigned
        </p>
        {adminIds.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              marginTop: "0.5rem",
            }}
          >
            {adminIds.map((id) => (
              <span
                key={id}
                style={{
                  display: "inline-block",
                  padding: "3px 10px",
                  background: "var(--second-background)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  border: "1px solid var(--border-color)",
                }}
              >
                {id === currentUserId ? "👤 You (ID: " : "👤 ID: "}
                {id}
                {id === currentUserId ? ")" : ""}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status timeline */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem 1.25rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          background: "var(--first-background)",
        }}
      >
        <h3 style={{ margin: "0 0 0.75rem" }}>📋 Status Timeline</h3>
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
        >
          {timelineEntries.map((entry, index) => {
            const entryStatus = getStatusStyle(entry.status);
            const isLast = index === timelineEntries.length - 1;
            return (
              <div
                key={entry.status}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.5rem 0.75rem",
                  background: isLast
                    ? "var(--second-background)"
                    : "transparent",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    background: isLast ? statusStyle.bg : "var(--second-color)",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: "14px" }}>
                    {entryStatus.icon} {entryStatus.label}
                  </strong>
                </div>
                <span
                  style={{ fontSize: "12px", color: "var(--second-color)" }}
                >
                  {formatDate(entry.date)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Approve button (only if user is admin and company is pending) */}
      {isAdmin && company.status === "pending" && (
        <>
          <ButtonLarge
            title="✅ Approve Company"
            onClick={() => setShowApproveModal(true)}
            mode="black"
            Accent={true}
          />

          {/* Approve confirmation modal */}
          {showApproveModal && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={() => {
                if (!approveLoading) setShowApproveModal(false);
              }}
            >
              <div
                style={{
                  background: "var(--first-background)",
                  padding: "1.5rem",
                  borderRadius: "12px",
                  maxWidth: "400px",
                  width: "90%",
                  textAlign: "center",
                }}
              >
                <h3 style={{ margin: "0 0 0.75rem" }}>⚠️ Confirm Approval</h3>
                <p
                  style={{
                    color: "var(--second-color)",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    margin: "0 0 0.75rem",
                  }}
                >
                  Are you sure you want to approve{" "}
                  <strong>{company.name}</strong>? This will create a business
                  instance, license, and grant you admin access in the desktop
                  application.
                </p>

                {approveError && (
                  <p
                    style={{
                      color: "var(--accent-color)",
                      fontSize: "14px",
                      margin: "0 0 0.75rem",
                    }}
                  >
                    ❌ {approveError}
                  </p>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    marginTop: "0.5rem",
                    justifyContent: "center",
                  }}
                >
                  <ButtonLarge
                    title="Cancel"
                    onClick={() => {
                      if (!approveLoading) {
                        setShowApproveModal(false);
                        setApproveError(null);
                      }
                    }}
                  />
                  <ButtonLarge
                    title="✅ Approve"
                    onClick={handleApprove}
                    Accent={true}
                    loading={approveLoading}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit button (only if user is admin) */}
      {isAdmin && (
        <ButtonLarge
          title="✏️ Edit Company"
          onClick={() =>
            navigate(getPath(ConfigRoutes.COMPANY_EDIT, { id: company.id }))
          }
          mode="black"
        />
      )}

      {/* Leave Company (только для member — не-админа). Админы сначала
          должны быть демоутнуты другим админом (см. backend CompanyService). */}
      {!isAdmin && (
        <>
          <ButtonLarge
            title="🚪 Leave Company"
            onClick={() => {
              setShowLeaveConfirm(true);
              setLeaveError(null);
            }}
            mode="black"
            Accent={true}
          />
          {showLeaveConfirm && (
            <PopUp
              content={
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: "32px", marginBottom: "0.5rem" }}>🚪</p>
                  <h3 style={{ margin: "0 0 0.75rem" }}>Leave Company?</h3>
                  <p
                    style={{
                      color: "var(--second-color)",
                      fontSize: "14px",
                      margin: "0 0 0.75rem",
                    }}
                  >
                    You will lose access to {company.name} and its shared
                    clouds.
                  </p>
                  {leaveError && (
                    <p
                      style={{
                        color: "var(--accent-color)",
                        fontSize: "14px",
                        margin: "0 0 0.75rem",
                      }}
                    >
                      ❌ {leaveError}
                    </p>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      justifyContent: "center",
                      marginTop: "0.5rem",
                    }}
                  >
                    <ButtonLarge
                      title="Cancel"
                      onClick={() => setShowLeaveConfirm(false)}
                      mode="white"
                      isSmall={true}
                    />
                    <ButtonLarge
                      title={leaveLoading ? "⏳ Leaving..." : "🚪 Leave"}
                      onClick={handleLeaveCompany}
                      mode="black"
                      Accent={true}
                      isSmall={true}
                      loading={leaveLoading}
                    />
                  </div>
                </div>
              }
              isCloseButton={true}
              onClick={() => {
                if (!leaveLoading) setShowLeaveConfirm(false);
              }}
            />
          )}
        </>
      )}

      {/* Navigation */}
      <ButtonLink
        title="← Back to Profile"
        onClick={() => navigate(getPath(ConfigRoutes.PROFILE))}
      />
    </div>
  );
};
